import React, { FC } from 'react';
import { OperationVariables } from 'react-apollo';
import { ApolloQueryResult } from 'apollo-client';
import { compose } from 'recompose';
import { Portal } from '@material-ui/core';
import { WithStyles, withStyles } from '@material-ui/core/styles';

import { GetProfilesByClientIdFullNameDocument, PublicUserProfile } from 'api';
import { withApolloClient, IWithApolloClient } from 'providers/withApolloClient';
import { getFullName } from 'helpers';
import { useCurrentUser } from 'hooks';

import { ISearchChange, IMentionPlugin } from '../types';
import styles from '../styles';
import Mention from './Mention';

interface IProps {
  toggleMentionState: (open: boolean) => void;
  users: PublicUserProfile[];
  onChange: (user: PublicUserProfile[]) => void;
  onAddMention: (user: PublicUserProfile) => void;
  plugin: IMentionPlugin;
}

interface IPropsEnhanced extends IProps, IWithApolloClient, WithStyles<typeof styles> {}

const enhancers = compose<IPropsEnhanced, IProps>(withApolloClient, withStyles(styles));

const Mentions: FC<IPropsEnhanced> = props => {
  const { toggleMentionState, apolloClient, onChange, onAddMention, plugin, users } = props;
  const currentUser = useCurrentUser();
  const formatted = getFormattedUsers();
  const { MentionSuggestions } = plugin;

  function getUsersExcludingCurrent() {
    return users.filter((clientUser: PublicUserProfile) => clientUser.id !== currentUser.id);
  }

  function getFormattedUsers() {
    const excludingCurrent = getUsersExcludingCurrent();
    const suggestions: PublicUserProfile[] = excludingCurrent || [];

    return suggestions.map((suggestion: PublicUserProfile) => {
      return {
        id: suggestion.id,
        name: getFullName(suggestion),
        full_name: suggestion.full_name,
        image: suggestion.image,
      };
    });
  }

  async function onSearchChange({ value }: ISearchChange) {
    if (value.length <= 1) {
      return;
    }

    const { data }: ApolloQueryResult<OperationVariables> = await apolloClient.query({
      query: GetProfilesByClientIdFullNameDocument,
      variables: { full_name: value },
    });

    const { getProfilesByClientIdFullName } = data;

    onChange(getProfilesByClientIdFullName);
  }

  return (
    <Portal>
      <MentionSuggestions
        onSearchChange={onSearchChange}
        suggestions={formatted}
        entryComponent={Mention}
        onAddMention={onAddMention}
        onOpen={() => toggleMentionState(true)}
        onClose={() => toggleMentionState(false)}
      />
    </Portal>
  );
};

export default enhancers(Mentions);
